import { DateTime } from 'luxon';

/** Calendar date without time zone, `YYYY-MM-DD`. */
export type LocalDate = string;
/** Wall-clock time without date, `HH:MM` (24h). */
export type LocalTime = string;
/** ISO weekday: 1 = Monday … 7 = Sunday. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const DEFAULT_ZONE = 'Europe/Ljubljana';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function utcDate(date: LocalDate): DateTime {
  return DateTime.fromISO(date, { zone: 'utc' });
}

function must(value: string | null, what: string): string {
  if (value === null) throw new Error(`Invalid ${what}`);
  return value;
}

export function isLocalDate(value: string): boolean {
  return DATE_RE.test(value) && utcDate(value).isValid;
}

export function isLocalTime(value: string): boolean {
  return TIME_RE.test(value);
}

/**
 * Converts a local date + wall-clock time in `zone` to an absolute instant.
 * Times that do not exist (spring-forward gap) are shifted forward by the gap, as Luxon does.
 */
export function toInstant(date: LocalDate, time: LocalTime, zone: string): DateTime {
  return DateTime.fromISO(`${date}T${time}`, { zone });
}

export function todayIn(zone: string, now: Date = new Date()): LocalDate {
  return must(DateTime.fromJSDate(now).setZone(zone).toISODate(), 'date');
}

export function nowTimeIn(zone: string, now: Date = new Date()): LocalTime {
  return DateTime.fromJSDate(now).setZone(zone).toFormat('HH:mm');
}

export function addDays(date: LocalDate, days: number): LocalDate {
  return must(utcDate(date).plus({ days }).toISODate(), 'date');
}

export function weekdayOf(date: LocalDate): Weekday {
  return utcDate(date).weekday as Weekday;
}

/** Monday of the ISO week containing `date`. */
export function startOfWeek(date: LocalDate): LocalDate {
  return addDays(date, 1 - weekdayOf(date));
}

/** Inclusive list of dates from `from` to `to`. */
export function dateRange(from: LocalDate, to: LocalDate): LocalDate[] {
  const out: LocalDate[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function timeToMinutes(time: LocalTime): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function minutesToTime(minutes: number): LocalTime {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** e.g. "četrtek, 1. oktober" */
export function formatDayLabel(date: LocalDate, locale = 'sl'): string {
  return utcDate(date).setLocale(locale).toFormat('cccc, d. LLLL');
}

/** e.g. "1. 10. 2026" */
export function formatShortDate(date: LocalDate): string {
  const d = utcDate(date);
  return `${d.day}. ${d.month}. ${d.year}`;
}

export function compareDates(a: LocalDate, b: LocalDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
